import { useState } from 'react';
import {
  listStudentDevices,
  revokeStudentDevice
} from './devices/deviceSessions';

function formatDate(value) {
  if (!value) return 'Unknown';

  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function platformLabel(value) {
  if (value === 'ios') return 'iOS';
  if (value === 'android') return 'Android';
  return value ? String(value) : 'Mobile';
}

function DeviceIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="6.5" y="2.5" width="11" height="19" rx="1.8" />
      <path d="M10 18.5h4" />
    </svg>
  );
}

export default function StudentDevices({ session, onBack }) {
  const [password, setPassword] = useState('');
  const [confirmedPassword, setConfirmedPassword] = useState('');
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  async function handleUnlock(e) {
    e.preventDefault();

    setLoading(true);
    setError('');

    try {
      const rows = await listStudentDevices({
        studentId: session.studentId,
        password
      });

      setDevices(rows);
      setConfirmedPassword(password);
      setPassword('');
    } catch (err) {
      setError(err?.message || 'Could not load devices.');
    } finally {
      setLoading(false);
    }
  }

  async function refreshDevices() {
    if (!confirmedPassword) return;

    setLoading(true);
    setError('');

    try {
      const rows = await listStudentDevices({
        studentId: session.studentId,
        password: confirmedPassword
      });

      setDevices(rows);
    } catch (err) {
      setError(err?.message || 'Could not refresh devices.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke(device) {
    const name = device.device_name || 'this device';

    if (!window.confirm(
      `Sign out ${name}? It will need to log in again before using the Veritas Student app.`
    )) {
      return;
    }

    setBusyId(device.id);
    setError('');

    try {
      await revokeStudentDevice({
        studentId: session.studentId,
        deviceSessionId: device.id,
        deviceSessionIds: device.session_ids,
        password: confirmedPassword
      });

      setDevices(current =>
        current.filter(item => item.id !== device.id)
      );
    } catch (err) {
      setError(err?.message || 'Could not sign out that device.');
    } finally {
      setBusyId(null);
    }
  }

  const unlocked = session.isMaster || Boolean(confirmedPassword);

  return (
    <div className="sd-shell">
      <style>{styles}</style>

      <header className="sd-topbar">
        <button type="button" onClick={onBack} className="sd-back">
          <span aria-hidden="true">←</span>
          Back
        </button>

        <div>
          <div className="sd-kicker">Student Account</div>
          <h1>Devices</h1>
        </div>
      </header>

      <main className="sd-main">
        {!unlocked ? (
          <section className="sd-auth-card">
            <div className="sd-icon-box">
              <DeviceIcon />
            </div>

            <h2>Manage signed-in devices</h2>

            <p>
              For security, confirm your password to view or sign out
              devices that are logged into the Veritas Student app.
            </p>

            <form onSubmit={handleUnlock}>
              <label htmlFor="devices-password">Password</label>

              <input
                id="devices-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                required
              />

              <button
                type="submit"
                className="sd-primary"
                disabled={loading}
              >
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </form>

            {error && <div className="sd-error">{error}</div>}
          </section>
        ) : (
          <>
            <section className="sd-heading-row">
              <div>
                <div className="sd-section-label">Signed in</div>
                <h2>Registered mobile devices</h2>
                <p>
                  Signing a device out removes its saved app login.
                  The student will need to sign in again on that device.
                </p>
              </div>

              <button
                type="button"
                className="sd-secondary"
                onClick={refreshDevices}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </section>

            {error && <div className="sd-error">{error}</div>}

            {devices.length === 0 ? (
              <div className="sd-empty">
                No mobile devices are currently signed in.
              </div>
            ) : (
              <div className="sd-device-list">
                {devices.map(device => (
                  <article className="sd-device" key={device.id}>
                    <div className="sd-device-icon">
                      <DeviceIcon />
                    </div>

                    <div className="sd-device-copy">
                      <strong>
                        {device.device_name ||
                          `${platformLabel(device.platform)} device`}
                      </strong>

                      <span>
                        {platformLabel(device.platform)}
                      </span>

                      <small>
                        Signed in {formatDate(device.created_at)}
                        <br />
                        Last used {formatDate(device.last_used_at)}
                      </small>
                    </div>

                    <button
                      type="button"
                      className="sd-revoke"
                      onClick={() => handleRevoke(device)}
                      disabled={busyId === device.id}
                    >
                      {busyId === device.id ? 'Signing out...' : 'Sign Out'}
                    </button>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const styles = `
  .sd-shell {
    min-height: 100vh;
    background: #f7f8fa;
    color: #192431;
    font-family: Inter, "Segoe UI", system-ui, sans-serif;
  }

  .sd-shell * {
    box-sizing: border-box;
  }

  .sd-topbar {
    min-height: 86px;
    display: grid;
    grid-template-columns: 160px 1fr 160px;
    align-items: center;
    gap: 20px;
    padding: 16px 32px;
    background: white;
    border-bottom: 1px solid #d9e2ec;
  }

  .sd-topbar > div {
    text-align: center;
  }

  .sd-kicker,
  .sd-section-label {
    color: #2f6f9f;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-size: 10px;
    font-weight: 800;
  }

  .sd-topbar h1,
  .sd-heading-row h2,
  .sd-auth-card h2 {
    font-family: Georgia, "Times New Roman", serif;
    color: #102a43;
    font-weight: 500;
  }

  .sd-topbar h1 {
    margin: 3px 0 0;
    font-size: 25px;
  }

  .sd-back,
  .sd-secondary {
    border: 1px solid #d9e2ec;
    border-radius: 5px;
    background: white;
    color: #334e68;
    padding: 9px 12px;
    font-weight: 700;
    cursor: pointer;
  }

  .sd-back {
    justify-self: start;
  }

  .sd-back span {
    margin-right: 6px;
  }

  .sd-main {
    width: min(920px, calc(100% - 40px));
    margin: 0 auto;
    padding: 42px 0 70px;
  }

  .sd-auth-card {
    width: min(440px, 100%);
    margin: 40px auto;
    padding: 28px;
    border: 1px solid #d9e2ec;
    border-radius: 6px;
    background: white;
  }

  .sd-icon-box,
  .sd-device-icon {
    display: grid;
    place-items: center;
    color: #2f6f9f;
    background: #eef4f8;
    border: 1px solid #d9e2ec;
  }

  .sd-icon-box {
    width: 46px;
    height: 46px;
    border-radius: 5px;
  }

  .sd-auth-card h2 {
    margin: 18px 0 8px;
    font-size: 23px;
  }

  .sd-auth-card p,
  .sd-heading-row p {
    color: #627d98;
    line-height: 1.55;
    font-size: 13px;
  }

  .sd-auth-card label {
    display: block;
    margin: 22px 0 7px;
    font-size: 12px;
    font-weight: 800;
    color: #334e68;
  }

  .sd-auth-card input {
    width: 100%;
    min-height: 42px;
    padding: 9px 11px;
    border: 1px solid #bcccdc;
    border-radius: 4px;
    font: inherit;
  }

  .sd-primary {
    width: 100%;
    margin-top: 12px;
    min-height: 42px;
    border: 0;
    border-radius: 4px;
    background: #102a43;
    color: white;
    font-weight: 800;
    cursor: pointer;
  }

  .sd-primary:disabled,
  .sd-secondary:disabled,
  .sd-revoke:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .sd-heading-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 20px;
  }

  .sd-heading-row h2 {
    margin: 4px 0 6px;
    font-size: 25px;
  }

  .sd-heading-row p {
    max-width: 600px;
    margin: 0;
  }

  .sd-device-list {
    border: 1px solid #d9e2ec;
    border-radius: 6px;
    overflow: hidden;
    background: white;
  }

  .sd-device {
    min-height: 106px;
    display: grid;
    grid-template-columns: 48px minmax(0, 1fr) auto;
    align-items: center;
    gap: 16px;
    padding: 18px;
    border-bottom: 1px solid #edf1f5;
  }

  .sd-device:last-child {
    border-bottom: 0;
  }

  .sd-device-icon {
    width: 44px;
    height: 44px;
    border-radius: 5px;
  }

  .sd-device-copy {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .sd-device-copy strong {
    color: #102a43;
    font-size: 14px;
  }

  .sd-device-copy span {
    color: #2f6f9f;
    font-size: 11px;
    font-weight: 800;
  }

  .sd-device-copy small {
    margin-top: 4px;
    color: #829ab1;
    font-size: 11px;
    line-height: 1.45;
  }

  .sd-revoke {
    border: 1px solid #d7a6a6;
    border-radius: 4px;
    background: white;
    color: #9d3434;
    padding: 9px 12px;
    font-weight: 800;
    cursor: pointer;
  }

  .sd-empty,
  .sd-error {
    border-radius: 5px;
    padding: 14px 16px;
    font-size: 13px;
  }

  .sd-empty {
    border: 1px solid #d9e2ec;
    background: white;
    color: #627d98;
  }

  .sd-error {
    margin-top: 14px;
    border: 1px solid #e4bcbc;
    background: #fbefef;
    color: #8f3030;
  }

  @media (max-width: 680px) {
    .sd-topbar {
      grid-template-columns: 90px 1fr 90px;
      padding: 12px 14px;
    }

    .sd-main {
      width: min(100% - 28px, 920px);
      padding-top: 24px;
    }

    .sd-heading-row {
      display: block;
    }

    .sd-secondary {
      margin-top: 14px;
    }

    .sd-device {
      grid-template-columns: 42px minmax(0, 1fr);
    }

    .sd-revoke {
      grid-column: 1 / -1;
      width: 100%;
    }
  }
`;
